import React from 'react';
import { Route, useRouteError, Routes as ReactRoutes } from 'react-router-dom';
import { useParams } from 'react-router';
import {
  ADD_SUB_PROFILE_PATH,
  ADD_USER_PATH,
  CONSENT_PATH,
  DEVICE_INFO_PATH,
  ERROR_PATH,
  FAVORITES_PATH,
  LIVE_PATH,
  LOGIN_ONBOARDING_PATH,
  LOGIN_PATH,
  PROFILES_PATH,
  SEARCH_PATH,
  SETTINGS_PATH,
  TESTS_PATH,
  TOS_PATH,
  RADIO_FRONT_PATH,
  BRAZE_INAPP_MESSAGE,
  HOME_PATH,
} from './routeNames';
import { routePathsById } from './getRoutePath';
import RootElement from './RootElement';
import { SuspenseWrapper } from './SuspenseWrapper';
import SettingsRoute from './SettingsRoute';
import { NotFoundRoute } from './NotFoundRoute';
import { ItemInfoRoute, ItemRoute } from './ItemRoute';
import EpisodesRoute from './EpisodesRoute';
import PlayerRoute from './PlayerRoute';
import ConsentRoute from './ConsentRoute';
import SimilarRoute from './SimilarRoute';
import {
  FavoritesRoute,
  LiveRoute,
  RadioFrontRoute,
  PackageRoute,
} from './ViewRoute';
import { useBraze } from '../braze/useBraze';
import { useEditorState } from '../EditProfile/useEditorState';
import TestPlayerRoute from './TestPlayerRoute';
import TestPageRoute from './TestPageRoute';
import BrazeRedirect from './redirects/BrazeRedirect';
import ProfilesRoute from './ProfilesRoute';
import LauncherRedirect from './redirects/LauncherRedirect';
import RootRoute from './RootRoute';

const AuthRequired = React.lazy(() => import('./redirects/AuthRedirect'));

const Search = React.lazy(() => import('../Search/SearchContainer'));

const SearchRoute = () => (
  <SuspenseWrapper>
    <AuthRequired>
      <Search />
    </AuthRequired>
  </SuspenseWrapper>
);

const LoginTunnusView = React.lazy(
  () => import('../AddUserView/LoginTunnusView/LoginTunnusView')
);

const LoginElement = () => (
  <SuspenseWrapper>
    <LoginTunnusView />
  </SuspenseWrapper>
);

const NotFoundLazy = React.lazy(() => import('../NotFound/NotFound'));

const NotFound = () => (
  <SuspenseWrapper>
    <NotFoundLazy />
  </SuspenseWrapper>
);

const LoginRootRoute = () => {
  return <LoginElement />;
};

const LoginOnboardingView = React.lazy(
  () => import('../LoginOnboarding/LoginOnboardingView')
);

const LoginOnboardingRoute = () => (
  <SuspenseWrapper>
    <LoginOnboardingView />
  </SuspenseWrapper>
);

const EditProfileView = React.lazy(
  () => import('../EditProfile/EditProfileView')
);

const InAppMessageView = React.lazy(
  () => import('../braze/InAppMessage/inAppMessageView/InAppMessageView')
);

const InAppMessageRoute = () => {
  const { inAppMessages, removeMessage, inAppMessageClick } = useBraze();

  if (inAppMessages[0]) {
    return (
      <SuspenseWrapper>
        <AuthRequired>
          <InAppMessageView
            inAppMessageClick={inAppMessageClick}
            message={inAppMessages[0]}
            removeMessage={removeMessage}
          />
        </AuthRequired>
      </SuspenseWrapper>
    );
  }

  return HomeRoute();
};

const ProfileEditRoute = () => {
  const { userId, fieldName } = useParams();
  const editorState = useEditorState(userId);

  if (!userId) {
    return <NotFound />;
  }

  return (
    <SuspenseWrapper>
      <EditProfileView
        userId={userId}
        displayNameUpdated={editorState.displayName}
        profileIconUpdated={editorState.profileIcon}
        updateDisplayName={editorState.updateDisplayName}
        updateProfileIcon={editorState.updateProfileIcon}
        setIsEditing={editorState.setIsEditing}
        closeNameEditor={editorState.closeNameEditor}
        clearUpdatedValues={editorState.clearUpdatedValues}
        fieldNameToEdit={fieldName}
      />
    </SuspenseWrapper>
  );
};

const AddUserViewContainer = React.lazy(
  () => import('../AddUserView/AddUserViewContainer')
);

const AddUserRoute = () => (
  <SuspenseWrapper>
    <AddUserViewContainer />
  </SuspenseWrapper>
);

const AddSubProfileView = React.lazy(
  () => import('../AddUserView/AddSubProfileView/AddSubProfileView')
);

const AddSubProfileRoute = () => (
  <SuspenseWrapper>
    <AddSubProfileView />
  </SuspenseWrapper>
);

const TosViewLazy = React.lazy(() => import('../TosView/TosView'));

const TermsOfServiceRoute = () => (
  <SuspenseWrapper>
    <AuthRequired>
      <TosViewLazy />
    </AuthRequired>
  </SuspenseWrapper>
);

const Home = React.lazy(() => import('../Home/Home'));

const HomeRoute = () => (
  <SuspenseWrapper>
    <AuthRequired>
      <LauncherRedirect>
        <BrazeRedirect>
          <Home />
        </BrazeRedirect>
      </LauncherRedirect>
    </AuthRequired>
  </SuspenseWrapper>
);

const DeviceInfo = React.lazy(() => import('../DeviceInfoView/DeviceInfoView'));

const DeviceInfoRoute = () => (
  <SuspenseWrapper>
    <DeviceInfo />
  </SuspenseWrapper>
);

const Error = React.lazy(() => import('../ErrorView/ErrorView'));

const ErrorRoute = () => (
  <SuspenseWrapper>
    <Error />
  </SuspenseWrapper>
);

function ErrorBoundaryWorkAround() {
  const error = useRouteError();
  if (error) {
    // Throw it up so our real ErrorBoundary can catch it
    throw error as Error;
  }
  return null;
}

const Routes = () => {
  return (
    <ReactRoutes>
      <Route
        element={<RootElement />}
        errorElement={<ErrorBoundaryWorkAround />}
      >
        <Route path={RADIO_FRONT_PATH} element={<RadioFrontRoute />} />
        <Route
          path={routePathsById.episodes(':itemId')}
          element={<EpisodesRoute />}
        />
        <Route
          path={routePathsById.info(':itemId')}
          element={<ItemInfoRoute />}
        />
        <Route path={routePathsById.item(':itemId')} element={<ItemRoute />} />
        <Route
          path={routePathsById.similar(':itemId')}
          element={<SimilarRoute />}
        />
        <Route
          path={routePathsById.player(':itemId')}
          element={<PlayerRoute />}
        />
        <Route path={`/${SEARCH_PATH}`} element={<SearchRoute />} />
        <Route path={`/${SETTINGS_PATH}`} element={<SettingsRoute />} />
        <Route path={`/${CONSENT_PATH}`} element={<ConsentRoute />} />
        <Route path={`/${LOGIN_PATH}`} element={<LoginRootRoute />} />
        <Route
          path={`/${BRAZE_INAPP_MESSAGE}`}
          element={<InAppMessageRoute />}
        />
        <Route
          path={`/${LOGIN_ONBOARDING_PATH}`}
          element={<LoginOnboardingRoute />}
        />
        <Route path={`/${PROFILES_PATH}`} element={<ProfilesRoute />} />
        <Route
          path={routePathsById.editProfile(':userId', ':fieldName?')}
          element={<ProfileEditRoute />}
        />
        <Route path={`/${ADD_USER_PATH}`} element={<AddUserRoute />} />
        <Route
          path={`/${ADD_SUB_PROFILE_PATH}`}
          element={<AddSubProfileRoute />}
        />
        <Route path={TOS_PATH} element={<TermsOfServiceRoute />} />
        <Route path={TESTS_PATH} element={<TestPageRoute />} />
        <Route
          path={routePathsById.testPlayer(':testId')}
          element={<TestPlayerRoute />}
        />
        <Route
          path={routePathsById.package(':viewId')}
          element={<PackageRoute />}
        />
        <Route path={LIVE_PATH} element={<LiveRoute />} />
        <Route path={FAVORITES_PATH} element={<FavoritesRoute />} />
        <Route path={DEVICE_INFO_PATH} element={<DeviceInfoRoute />} />
        <Route path={ERROR_PATH} element={<ErrorRoute />} />
        <Route path={HOME_PATH} element={<HomeRoute />} />
        <Route path="/" element={<RootRoute />} />
        <Route path={`*`} element={<NotFoundRoute />} />
      </Route>
    </ReactRoutes>
  );
};

export default Routes;
